/**
 * CreateOpportunityModal - Modal for creating a new pipeline opportunity.
 * Includes contact picker with search, stage selector, value, source, and notes.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Plus, Search, User, AlertCircle, Loader, Target
} from 'lucide-react';

const SOURCE_OPTIONS = [
  'Website', 'Referral', 'Social Media', 'Cold Outreach', 'Event', 'Partner', 'Other'
];

function CreateOpportunityModal({ isOpen, onClose, apiWithAccessControl, onCreated }) {
  // Form state
  const [form, setForm] = useState({
    title: '',
    contactId: '',
    contactType: '',
    stageId: '',
    expectedValue: '',
    source: '',
    notes: '',
    expectedCloseDate: ''
  });

  // Contact picker
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [showContactResults, setShowContactResults] = useState(false);
  const contactSearchTimer = useRef(null);
  const contactPickerRef = useRef(null);

  // Stages
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errors, setErrors] = useState({});

  // Load stages on mount
  useEffect(() => {
    if (!isOpen) return;
    const loadStages = async () => {
      setLoadingStages(true);
      try {
        const res = await apiWithAccessControl('/admin/pipeline/stages');
        const data = await res.json();
        if (data.success !== false) {
          const stageList = data.stages || [];
          setStages(stageList);
          // Default to first stage
          if (stageList.length > 0 && !form.stageId) {
            setForm(prev => ({ ...prev, stageId: String(stageList[0].id) }));
          }
        }
      } catch (err) {
        console.error('Failed to load stages:', err);
      } finally {
        setLoadingStages(false);
      }
    };
    loadStages();
  }, [isOpen, apiWithAccessControl]);

  // Auto-dismiss status
  useEffect(() => {
    if (statusMsg) {
      const timer = setTimeout(() => setStatusMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMsg]);

  // Contact search with debounce
  const searchContacts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContactResults([]);
      setShowContactResults(false);
      return;
    }
    setSearchingContacts(true);
    try {
      const res = await apiWithAccessControl(`/admin/contacts?search=${encodeURIComponent(query)}&contactType=ALL`);
      const data = await res.json();
      if (data.success !== false) {
        setContactResults(data.contacts || []);
        setShowContactResults(true);
      }
    } catch (err) {
      console.error('Contact search error:', err);
    } finally {
      setSearchingContacts(false);
    }
  }, [apiWithAccessControl]);

  const handleContactSearchChange = (e) => {
    const val = e.target.value;
    setContactSearch(val);
    if (contactSearchTimer.current) clearTimeout(contactSearchTimer.current);
    contactSearchTimer.current = setTimeout(() => searchContacts(val), 300);
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setForm(prev => ({
      ...prev,
      contactId: String(contact.id),
      contactType: contact.contactType || 'LEAD'
    }));
    setShowContactResults(false);
    setContactSearch('');
  };

  const clearContact = () => {
    setSelectedContact(null);
    setForm(prev => ({ ...prev, contactId: '' }));
  };

  // Close contact results on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (contactPickerRef.current && !contactPickerRef.current.contains(e.target)) {
        setShowContactResults(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Validation
  const validate = () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = 'Title is required';
    if (!form.stageId) newErrors.stageId = 'Stage is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setStatusMsg(null);

    try {
      const payload = {
        title: form.title.trim(),
        stageId: form.stageId,
        ...(form.contactId && { contactId: form.contactId }),
        ...(form.contactType && { contactType: form.contactType }),
        ...(form.expectedValue && { expectedValue: Number(form.expectedValue) }),
        ...(form.source && { source: form.source }),
        ...(form.notes.trim() && { notes: form.notes.trim() }),
        ...(form.expectedCloseDate && { expectedCloseDate: form.expectedCloseDate })
      };

      const res = await apiWithAccessControl('/admin/pipeline/opportunities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success !== false) {
        onCreated && onCreated(data.opportunity);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to create opportunity' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to create opportunity. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset on close
  const handleClose = () => {
    setForm({ title: '', contactId: '', contactType: '', stageId: '', expectedValue: '', source: '', notes: '', expectedCloseDate: '' });
    setSelectedContact(null);
    setContactSearch('');
    setContactResults([]);
    setErrors({});
    setStatusMsg(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mkt-pipe-modal-overlay" onClick={handleClose}>
      <div className="mkt-pipe-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mkt-pipe-modal-header">
          <h2>New Opportunity</h2>
          <button className="mkt-pipe-btn-icon" onClick={handleClose} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="mkt-pipe-modal-body">
            {statusMsg && (
              <div className={`mkt-pipe-status-msg ${statusMsg.type}`} style={{ marginBottom: 16 }}>
                {statusMsg.type === 'error' ? <AlertCircle size={14} /> : <Target size={14} />}
                {statusMsg.text}
              </div>
            )}

            {/* Title */}
            <div className="mkt-pipe-form-group">
              <label className="mkt-pipe-form-label">Title *</label>
              <input
                type="text"
                className="mkt-pipe-form-input"
                placeholder="e.g., Acme Corp - Premium Plan"
                value={form.title}
                onChange={(e) => {
                  setForm(prev => ({ ...prev, title: e.target.value }));
                  if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                }}
                autoFocus
              />
              {errors.title && (
                <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>{errors.title}</span>
              )}
            </div>

            {/* Contact Picker */}
            <div className="mkt-pipe-form-group">
              <label className="mkt-pipe-form-label">Contact</label>
              {selectedContact ? (
                <div className="mkt-pipe-contact-selected">
                  <User size={16} style={{ color: '#64748b' }} />
                  <div className="info">
                    <div className="name">{selectedContact.name || selectedContact.email}</div>
                    {selectedContact.email && <div className="email">{selectedContact.email}</div>}
                  </div>
                  {selectedContact.contactType && (
                    <span className={`mkt-pipe-type-badge ${selectedContact.contactType === 'LEAD' ? 'lead' : 'waitlist'}`}>
                      {selectedContact.contactType}
                    </span>
                  )}
                  <button
                    type="button"
                    className="mkt-pipe-btn-icon"
                    style={{ width: 24, height: 24 }}
                    onClick={clearContact}
                    title="Remove contact"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="mkt-pipe-contact-picker" ref={contactPickerRef}>
                  <div className="mkt-pipe-search-wrap" style={{ width: '100%' }}>
                    <Search size={14} className="mkt-pipe-search-icon" />
                    <input
                      type="text"
                      className="mkt-pipe-search-input"
                      style={{ width: '100%' }}
                      placeholder="Search contacts by name or email..."
                      value={contactSearch}
                      onChange={handleContactSearchChange}
                      onFocus={() => contactResults.length > 0 && setShowContactResults(true)}
                    />
                  </div>
                  {showContactResults && contactResults.length > 0 && (
                    <div className="mkt-pipe-contact-results">
                      {contactResults.map(c => (
                        <div
                          key={c.id}
                          className="mkt-pipe-contact-result-item"
                          onClick={() => selectContact(c)}
                        >
                          <span className="name">{c.name || c.email}</span>
                          {c.email && c.name && <span className="email">{c.email}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {showContactResults && contactResults.length === 0 && contactSearch.length >= 2 && !searchingContacts && (
                    <div className="mkt-pipe-contact-results">
                      <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        No contacts found
                      </div>
                    </div>
                  )}
                  {searchingContacts && (
                    <div className="mkt-pipe-contact-results">
                      <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        Searching...
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Stage */}
            <div className="mkt-pipe-form-group">
              <label className="mkt-pipe-form-label">Stage *</label>
              <select
                className="mkt-pipe-form-select"
                value={form.stageId}
                onChange={(e) => {
                  setForm(prev => ({ ...prev, stageId: e.target.value }));
                  if (errors.stageId) setErrors(prev => ({ ...prev, stageId: null }));
                }}
              >
                <option value="">Select a stage</option>
                {stages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {errors.stageId && (
                <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>{errors.stageId}</span>
              )}
            </div>

            {/* Value & Source */}
            <div className="mkt-pipe-form-row">
              <div className="mkt-pipe-form-group">
                <label className="mkt-pipe-form-label">Expected Value (₦)</label>
                <input
                  type="number"
                  className="mkt-pipe-form-input"
                  placeholder="0"
                  min="0"
                  value={form.expectedValue}
                  onChange={(e) => setForm(prev => ({ ...prev, expectedValue: e.target.value }))}
                />
              </div>
              <div className="mkt-pipe-form-group">
                <label className="mkt-pipe-form-label">Source</label>
                <select
                  className="mkt-pipe-form-select"
                  value={form.source}
                  onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))}
                >
                  <option value="">Select source</option>
                  {SOURCE_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Expected Close Date */}
            <div className="mkt-pipe-form-group">
              <label className="mkt-pipe-form-label">Expected Close Date</label>
              <input
                type="date"
                className="mkt-pipe-form-input"
                value={form.expectedCloseDate}
                onChange={(e) => setForm(prev => ({ ...prev, expectedCloseDate: e.target.value }))}
              />
            </div>

            {/* Notes */}
            <div className="mkt-pipe-form-group">
              <label className="mkt-pipe-form-label">Notes</label>
              <textarea
                className="mkt-pipe-form-textarea"
                placeholder="Add any additional notes..."
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="mkt-pipe-modal-footer">
            <button
              type="button"
              className="mkt-pipe-btn-secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="mkt-pipe-btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader size={14} style={{ animation: 'mkt-pipe-spin 0.7s linear infinite' }} />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={14} />
                  Create Opportunity
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateOpportunityModal;
